import { type ReactNode } from 'react';
import { Icon, type IconName } from './Icon.js';
import { cx } from './utils.js';

export type TabItem = {
  id: string;
  label: string;
  icon?: IconName;
  tone?: 'default' | 'danger';
};

export type TabsProps = {
  items: readonly TabItem[];
  value: string;
  onChange: (id: string) => void;
  className?: string;
  /**
   * `underline` — page sections (Settings, site detail).
   * `pills` — filter chips (Knowledge kinds).
   */
  variant?: 'underline' | 'pills';
};

export function Tabs({
  items,
  value,
  onChange,
  className,
  variant = 'underline',
}: TabsProps): ReactNode {
  if (variant === 'pills') {
    return (
      <div
        className={cx('flex flex-wrap gap-1.5', className)}
        role="tablist"
        data-ui="tabs"
        data-variant="pills"
      >
        {items.map((item) => {
          const active = item.id === value;
          const danger = item.tone === 'danger';
          return (
            <button
              key={item.id}
              type="button"
              role="tab"
              aria-selected={active}
              onClick={() => onChange(item.id)}
              className={cx(
                'inline-flex items-center gap-1.5 rounded-[var(--admin-radius-sm)] border px-2.5 py-1 text-[12px] font-medium transition',
                active &&
                  !danger &&
                  'border-[color:var(--admin-accent,#2563EB)] bg-[color:var(--admin-accent,#2563EB)] text-white',
                active && danger && 'border-rose-600 bg-rose-600 text-white',
                !active &&
                  !danger &&
                  'border-[color:var(--admin-border)] bg-[color:var(--admin-surface,#fff)] text-slate-600 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-400 dark:hover:bg-slate-800',
                !active &&
                  danger &&
                  'border-rose-200 bg-[color:var(--admin-surface,#fff)] text-rose-600 hover:bg-rose-50 dark:border-rose-900 dark:bg-slate-900 dark:text-rose-300 dark:hover:bg-rose-950/40',
              )}
            >
              {item.icon ? <Icon name={item.icon} className="size-3.5" /> : null}
              {item.label}
            </button>
          );
        })}
      </div>
    );
  }

  return (
    <div
      className={cx(
        'flex flex-wrap gap-1 border-b border-[color:var(--admin-border)] dark:border-slate-700',
        className,
      )}
      role="tablist"
      data-ui="tabs"
      data-variant="underline"
    >
      {items.map((item) => {
        const active = item.id === value;
        const danger = item.tone === 'danger';
        return (
          <button
            key={item.id}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onChange(item.id)}
            className={cx(
              'inline-flex items-center gap-1.5 px-3 py-2 text-sm transition',
              active &&
                !danger &&
                'border-b-2 border-[color:var(--admin-accent,#2563EB)] font-medium text-[color:var(--admin-accent,#2563EB)]',
              active && danger && 'border-b-2 border-rose-600 font-medium text-rose-700 dark:text-rose-300',
              !active &&
                !danger &&
                'text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-200',
              !active && danger && 'text-rose-500 hover:text-rose-700 dark:text-rose-400',
            )}
          >
            {item.icon ? <Icon name={item.icon} className="size-4" /> : null}
            {item.label}
          </button>
        );
      })}
    </div>
  );
}
